/**
 * LeadSourceReport (LEAD-SOURCE-02, Seth 2026-09-01: "Are lead sources
 * reportable?") — the whole customer book by where they came from: total
 * customers, new this year, and hand-corrections per source. Same honest
 * attribution as the 360 card (override wins; else the FIRST invoice's code
 * only when it's a real source — never "existing customer").
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, Download } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { supabase } from "@/lib/supabase";
import { downloadCsv } from "./csv";

interface Row {
  code: string;
  label: string;
  category: string | null;
  active: boolean;
  customers: number;
  new_this_year: number;
  overrides: number;
}

function useLeadSourceReport() {
  return useQuery({
    queryKey: ["lead_source_report"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase.rpc("lead_source_report");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });
}

export function LeadSourceReport() {
  const { data, isLoading } = useLeadSourceReport();
  const rows = useMemo(() => (data ?? []).filter((r) => r.customers > 0 || r.active), [data]);
  const totals = useMemo(
    () => rows.reduce((a, r) => ({ customers: a.customers + r.customers, ny: a.ny + r.new_this_year }), { customers: 0, ny: 0 }),
    [rows],
  );
  const year = new Date().getFullYear();

  const exportCsv = () =>
    downloadCsv(
      `lead-sources-${year}.csv`,
      [
        ["Source", "Code", "Customers", `New in ${year}`, "Hand-corrected"],
        ...rows.map((r) => [r.label, r.code, r.customers, r.new_this_year, r.overrides] as (string | number)[]),
      ],
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <Megaphone className="h-5 w-5" />
          </span>
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-900">
              Lead Sources
              <InfoTip label="How customers are counted">
                A correction made on the customer&apos;s page wins; otherwise their first invoice&apos;s source counts, if it&apos;s a real source
                (never &ldquo;existing customer&rdquo; or &ldquo;other&rdquo;). Customers with no source aren&apos;t listed until one is set on
                their page. Edit the list in Settings → Lead Sources.
              </InfoTip>
            </h3>
            <p className="text-xs text-slate-500">
              {totals.customers.toLocaleString()} attributed customers · {totals.ny.toLocaleString()} new in {year}
            </p>
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={exportCsv} disabled={rows.length === 0}>
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hairline bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-2.5">Source</th>
              <th className="px-3 py-2.5 text-right">Customers</th>
              <th className="px-3 py-2.5 text-right">Share</th>
              <th className="px-3 py-2.5 text-right">New in {year}</th>
              <th className="px-3 py-2.5 text-right">Corrected by hand</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">Counting the whole book…</td></tr>
            )}
            {rows.map((r) => {
              const share = totals.customers > 0 ? (r.customers / totals.customers) * 100 : 0;
              return (
                <tr key={r.code} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-slate-800">{r.label}</span>
                    <span className="ml-2 font-mono text-[11px] text-slate-300">{r.code}</span>
                    {!r.active && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">archived</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-800">{r.customers.toLocaleString()}</td>
                  <td className="px-3 py-2.5">
                    <span className="flex items-center justify-end gap-2">
                      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                        <span className="block h-full rounded-full bg-amber-400" style={{ width: `${Math.max(2, share)}%` }} />
                      </span>
                      <span className="w-10 text-right text-xs tabular-nums text-slate-500">{share >= 1 ? Math.round(share) : share > 0 ? "<1" : 0}%</span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.new_this_year.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">{r.overrides || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
